import type { PlayerTelemetry } from '@/core/types';
import { clamp01 } from '@/utils/math';

/**
 * The city's sensory memory of the player.
 *
 * Every frame of telemetry is folded into a small set of exponential moving
 * averages (EMAs) and counters. EMAs give the city a *fading* memory — it weights
 * recent behavior more heavily than the distant past, so if you change how you play
 * the city eventually notices and changes its mind. The counters (visited cells,
 * jumps, distance) capture lifetime facts that shouldn't decay.
 *
 * This class deliberately holds only observations, never conclusions. Turning
 * observations into inferred preferences is the analyzer's job — keeping the two
 * apart means we can retune the inference without touching the sensing.
 */

/** Time constants (seconds) for the fading memory of each signal. Short enough that
 *  the city reacts to how you're moving *now*, long enough not to twitch. */
const TAU_FAST = 6;
const TAU_SLOW = 22;

export interface BehaviorSnapshot {
  /** Fraction of recent time spent moving meaningfully. */
  activity: number;
  /** Recent average horizontal speed (m/s). */
  avgSpeed: number;
  /** Recent fraction of time spent elevated (roofs / in air). */
  elevatedFrac: number;
  /** Recent average height above ground (m). */
  avgHeight: number;
  /** Recent openness of surroundings [0,1]; low = alleys. */
  openness: number;
  /** Recent fraction of time spent still and regarding the view. */
  gazeFrac: number;
  /** Recent fraction of time spent looking distinctly upward — a longing for
   *  height the city can act on even before the player can reach any roof. */
  upGazeFrac: number;
  /** Jumps per second, recent. */
  jumpRate: number;
  /** Novelty: rate of entering unseen cells, recent. */
  noveltyRate: number;
  /** Lifetime distinct cells visited (coverage). */
  cellsVisited: number;
  /** Lifetime distance travelled (m). */
  distance: number;
  /** Seconds observed. */
  age: number;
}

export class BehaviorMemory {
  private emaActivity = 0;
  private emaSpeed = 0;
  private emaElevated = 0;
  private emaHeight = 0;
  private emaOpenness = 0.7;
  private emaGaze = 0;
  private emaUpGaze = 0;
  private emaJump = 0;
  private emaNovelty = 0;

  private visited = new Set<number>();
  private jumps = 0;
  private distance = 0;
  private age = 0;

  private lastPos = { x: 0, z: 0 };
  private hasLast = false;

  ingest(t: PlayerTelemetry): void {
    const dt = t.dt;
    if (dt <= 0) return;
    this.age += dt;

    const aFast = 1 - Math.exp(-dt / TAU_FAST);
    const aSlow = 1 - Math.exp(-dt / TAU_SLOW);

    // Instantaneous signals.
    const moving = t.speed > 0.5 ? 1 : 0;
    const gazing = t.still && Math.abs(t.lookPitch) > 0.18 ? 1 : 0;
    const upGazing = t.lookPitch > 0.3 ? 1 : 0;
    const jumped = t.jumped ? 1 : 0;

    // Cell novelty.
    const cellKey = this.cellKey(t.position.x, t.position.z);
    let novelty = 0;
    if (!this.visited.has(cellKey)) {
      this.visited.add(cellKey);
      novelty = 1;
    }

    // Distance.
    if (this.hasLast) {
      this.distance += Math.hypot(t.position.x - this.lastPos.x, t.position.z - this.lastPos.z);
    }
    this.lastPos.x = t.position.x;
    this.lastPos.z = t.position.z;
    this.hasLast = true;

    if (jumped) this.jumps++;

    // Fold into EMAs. Rates (jump, novelty) are divided by dt so they read as
    // "per second" regardless of frame rate, then smoothed.
    this.emaActivity += (moving - this.emaActivity) * aSlow;
    this.emaSpeed += (t.speed - this.emaSpeed) * aFast;
    this.emaElevated += ((t.elevated ? 1 : 0) - this.emaElevated) * aSlow;
    this.emaHeight += (t.height - this.emaHeight) * aFast;
    this.emaOpenness += (t.enclosure - this.emaOpenness) * aFast;
    this.emaGaze += (gazing - this.emaGaze) * aSlow;
    this.emaUpGaze += (upGazing - this.emaUpGaze) * aSlow;
    this.emaJump += (jumped / dt - this.emaJump) * aFast;
    this.emaNovelty += (novelty / dt - this.emaNovelty) * aSlow;
  }

  private cellKey(x: number, z: number): number {
    // 6m buckets — finer than a city cell so movement within a block still counts.
    const gx = Math.round(x / 6) + 512;
    const gz = Math.round(z / 6) + 512;
    return gx * 1024 + gz;
  }

  snapshot(): BehaviorSnapshot {
    return {
      activity: clamp01(this.emaActivity),
      avgSpeed: Math.max(0, this.emaSpeed),
      elevatedFrac: clamp01(this.emaElevated),
      avgHeight: Math.max(0, this.emaHeight),
      openness: clamp01(this.emaOpenness),
      gazeFrac: clamp01(this.emaGaze),
      upGazeFrac: clamp01(this.emaUpGaze),
      jumpRate: Math.max(0, this.emaJump),
      noveltyRate: Math.max(0, this.emaNovelty),
      cellsVisited: this.visited.size,
      distance: this.distance,
      age: this.age,
    };
  }

  serialize(): object {
    return {
      emaActivity: this.emaActivity,
      emaSpeed: this.emaSpeed,
      emaElevated: this.emaElevated,
      emaHeight: this.emaHeight,
      emaOpenness: this.emaOpenness,
      emaGaze: this.emaGaze,
      emaUpGaze: this.emaUpGaze,
      emaJump: this.emaJump,
      emaNovelty: this.emaNovelty,
      jumps: this.jumps,
      distance: this.distance,
      age: this.age,
      visited: [...this.visited],
    };
  }

  restore(data: ReturnType<BehaviorMemory['serialize']>): void {
    const d = data as Record<string, number> & { visited: number[] };
    this.emaActivity = d.emaActivity ?? 0;
    this.emaSpeed = d.emaSpeed ?? 0;
    this.emaElevated = d.emaElevated ?? 0;
    this.emaHeight = d.emaHeight ?? 0;
    this.emaOpenness = d.emaOpenness ?? 0.7;
    this.emaGaze = d.emaGaze ?? 0;
    this.emaUpGaze = d.emaUpGaze ?? 0;
    this.emaJump = d.emaJump ?? 0;
    this.emaNovelty = d.emaNovelty ?? 0;
    this.jumps = d.jumps ?? 0;
    this.distance = d.distance ?? 0;
    this.age = d.age ?? 0;
    this.visited = new Set(d.visited ?? []);
  }
}
